'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import React, { useCallback, useEffect } from 'react';

// 导入页面缓存 hook
import { usePageCache } from '@/hooks/usePageCache';
import dynamic from 'next/dynamic';
import CategoryNavBig from './components/CategoryNavBig/CategoryNavBig';
import { useHomeSensorsTracking } from './hooks/useHomeSensorsTracking';
import { useHomeThirdPartyTracking } from './hooks/useHomeThirdPartyTracking';

// 直接导入所有组件 - 现在全部同步加载以优化SEO
import { useTranslations } from 'next-intl';
const BlogArticle = dynamic(() => import('./components/BlogArticle/BlogArticle'), { ssr: true });
const BrandImage = dynamic(() => import('./components/BrandImage/BrandImage'), { ssr: true });
const CategoryNavShape = dynamic(() => import('./components/CategoryNavShape/CategoryNavShape'), {
  ssr: true,
});
const CategoryNavSmall = dynamic(() => import('./components/CategoryNavSmall/CategoryNavSmall'), {
  ssr: true,
});
const FivePicSwiper = dynamic(() => import('./components/FivePicSwiper/FivePicSwiper'), {
  ssr: true,
});
const HeroBanner = dynamic(() => import('./components/HomeBanner/HomeBanner'), { ssr: true });
const MultiSwipe = dynamic(() => import('./components/MultiSwipe/MultiSwipe'), { ssr: true });
const ProductList = dynamic(() => import('./components/ProductList'), { ssr: true });
const StyleHub = dynamic(() => import('./components/StyleHub/StyleHub'), { ssr: true });
const WaterFall = dynamic(() => import('./components/WaterFall/WaterFall'), { ssr: true });

// 首页组件数据类型定义
interface HomePageBlockItem {
  id?: number;
  component_id?: string;
  jump_target?: any[];
  [key: string]: any;
}

// 首页组件的 Props 类型定义
interface StreamingHomePageProps {
  homeListData: HomePageBlockItem[];
  deviceType?: EPlatform;
}

// 组件映射表 - 将后端返回的 component_id 映射到具体的 React 组件
const COMPONENT_MAP: Record<string, React.ComponentType<any>> = {
  swipeImage: HeroBanner,
  categoryNavSmall: CategoryNavSmall,
  categoryNavShape: CategoryNavShape,
  categoryNavBig: CategoryNavBig,
  styleHub: StyleHub,
  multiSwipe: MultiSwipe,
  multiSwipeSecond: FivePicSwiper,
  blogArticle: BlogArticle,
  brandImage: BrandImage,
  regularList: ProductList,
  waterFall: WaterFall,
};

// 主要的首页组件
export default function StreamingHomePage({
  homeListData,
  deviceType = EPlatform.WEB,
}: StreamingHomePageProps) {
  const isMobile = deviceType === EPlatform.WAP;
  const t = useTranslations('homePage');

  // 🔍 使用首页神策埋点hook
  const { trackHomeView, trackHomeMktExp, trackHomeMktClick, trackHomeMktValidExp } =
    useHomeSensorsTracking();

  // 🔍 使用首页第三方埋点hook
  const { initHomeThirdPartyTracking } = useHomeThirdPartyTracking();

  // 统一的埋点处理函数
  const handleSDHomeMktClick = useCallback(
    (eventParams: any) => {
      trackHomeMktClick(eventParams);
    },
    [trackHomeMktClick],
  );

  const handleSDHomeMktExp = useCallback(
    (eventParams: any) => {
      trackHomeMktExp(eventParams);
    },
    [trackHomeMktExp],
  );

  const handleSDHomeMktValidExp = useCallback(
    (eventParams: any) => {
      trackHomeMktValidExp(eventParams);
    },
    [trackHomeMktValidExp],
  );

  // 🔍 首页埋点：页面加载完成后触发Home_View埋点
  useEffect(() => {
    // 使用requestIdleCallback优化埋点执行时机
    const scheduleTracking = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            trackHomeView();
          },
          { timeout: 500 },
        );
      } else {
        // 降级到setTimeout
        setTimeout(trackHomeView, 100);
      }
    };

    scheduleTracking();

    // 延迟加载第三方埋点，避免阻塞主线程
    const thirdPartyTimer = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            initHomeThirdPartyTracking();
          },
          { timeout: 1000 },
        );
      } else {
        initHomeThirdPartyTracking();
      }
    }, 1000); // 延迟1秒加载第三方埋点

    return () => {
      clearTimeout(thirdPartyTimer);
    };
  }, [trackHomeView, initHomeThirdPartyTracking]);

  // 使用页面缓存 hook 来保存和恢复滚动位置
  const { pageState } = usePageCache({
    pageKey: 'zl-homepage',
    initialData: {
      homeListData,
    },
  });

  // 数据验证：如果没有数据则显示空状态
  if (!pageState.homeListData || pageState.homeListData.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500">{t('noContent')}</div>
        </div>
      </div>
    );
  }

  return (
    <div id="main-box" className="homepage w-full">
      {/* 按照后端返回的顺序渲染组件，保持内容的逻辑顺序 */}
      {pageState.homeListData.map((item, index) => {
        if (!item.component_id) {
          return null;
        }

        const Component = COMPONENT_MAP[item.component_id];
        if (!Component) {
          return null;
        }

        return (
          <div key={item.id || index} className="index-box">
            <Component
              moduleIndex={index}
              indexData={item}
              homeListData={pageState.homeListData}
              isMobile={isMobile}
              onTrack={handleSDHomeMktClick}
              onExpTrack={handleSDHomeMktExp}
              onValidExpTrack={handleSDHomeMktValidExp}
            />
          </div>
        );
      })}
    </div>
  );
}

// 保持兼容性的Provider组件
export function StreamingProvider({ children }: { children: React.ReactNode }) {
  return <div className="homepage-provider">{children}</div>;
}
